import { getLocale } from '../i18n/localization';

export function formatDuration(ms?: number | null): string {
  if (ms === null || ms === undefined) return '—';

  const units = [
    { label: 'h', value: 60 * 60 * 1000 },
    { label: 'm', value: 60 * 1000 },
    { label: 's', value: 1000 },
    { label: 'ms', value: 1 },
  ];

  const parts: string[] = [];
  let remaining = ms;

  for (const { label, value } of units) {
    const amount = Math.floor(remaining / value);
    remaining %= value;

    if (amount > 0) {
      parts.push(`${amount}${label}`);
    }
  }

  return parts.length ? parts.join(' ') : '0ms';
}

export const formatCurrencyCents = (
  value: number | null | undefined,
  currency = 'EUR',
  locale: string = getLocale()
) => {
  if (typeof value !== 'number') return '—';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(value / 100);
};

type DateValue = string | Date | null | undefined;

function toValidDate(value: DateValue): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDateTime(
  value: DateValue,
  options: Intl.DateTimeFormatOptions = {
    dateStyle: 'medium',
    timeStyle: 'short',
  },
  locale: string = getLocale()
): string {
  if (!value) return '—';
  const date = toValidDate(value);
  if (!date) return String(value);
  try {
    return new Intl.DateTimeFormat(locale, options).format(date);
  } catch {
    return date.toLocaleString(locale);
  }
}

export function formatDate(
  value: DateValue,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'short' },
  locale: string = getLocale()
): string {
  return formatDateTime(value, options, locale);
}

export interface NaturalDate {
  label: string;
  title: string;
}

interface NaturalDateOptions {
  locale?: string;
  now?: Date;
}

const startOfLocalDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const isSameLocalDay = (left: Date, right: Date): boolean =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const capitalizeFirst = (value: string): string =>
  value ? value[0].toLocaleUpperCase() + value.slice(1) : value;

const formatAgo = (
  amount: number,
  unit: Intl.RelativeTimeFormatUnit,
  locale?: string
): string =>
  capitalizeFirst(
    new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(
      -amount,
      unit
    )
  );

export function formatNaturalDate(
  value?: string | Date | null,
  options: NaturalDateOptions = {}
): NaturalDate {
  if (!value) return { label: '—', title: '—' };

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    const fallback = String(value);
    return { label: fallback, title: fallback };
  }

  const now = options.now ?? new Date();
  const locale = options.locale ?? getLocale();
  const title = date.toLocaleString(locale, {
    dateStyle: 'full',
    timeStyle: 'medium',
  });

  if (isSameLocalDay(date, now)) {
    return {
      label: date.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
      }),
      title,
    };
  }

  const yesterday = startOfLocalDay(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (isSameLocalDay(date, yesterday)) {
    return { label: formatAgo(1, 'day', locale), title };
  }

  const dayMs = 24 * 60 * 60 * 1000;
  const dayDiff = Math.floor(
    (startOfLocalDay(now).getTime() - startOfLocalDay(date).getTime()) / dayMs
  );

  if (dayDiff > 0 && dayDiff < 7) {
    return { label: formatAgo(dayDiff, 'day', locale), title };
  }

  if (dayDiff >= 7 && dayDiff < 56) {
    return { label: formatAgo(Math.floor(dayDiff / 7), 'week', locale), title };
  }

  if (dayDiff >= 56) {
    let monthDiff =
      (now.getFullYear() - date.getFullYear()) * 12 +
      now.getMonth() -
      date.getMonth();
    if (date.getDate() > now.getDate()) {
      monthDiff -= 1;
    }
    return { label: formatAgo(Math.max(1, monthDiff), 'month', locale), title };
  }

  return {
    label: date.toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }),
    title,
  };
}
